import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import subscriptionService from '@/services/subscriptionService';

interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string;
  patientName: string;
  onSuccess?: () => void;
}

const CANCELLATION_REASONS = [
  { value: 'patient_request', label: 'Patient requested cancellation' },
  { value: 'medical_reason', label: 'Medical reason / treatment change' },
  { value: 'payment_issues', label: 'Payment issues' },
  { value: 'product_unavailable', label: 'Product no longer available' },
  { value: 'side_effects', label: 'Side effects or adverse reactions' },
  { value: 'no_longer_needed', label: 'Medication no longer needed' },
  { value: 'other', label: 'Other reason' },
];

const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  open,
  onOpenChange,
  subscriptionId,
  patientName,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (!reason) {
      toast({
        title: 'Reason Required',
        description: 'Please select a cancellation reason',
        variant: 'destructive',
      });
      return;
    }

    setCancelling(true);
    try {
      const fullReason = additionalNotes 
        ? `${CANCELLATION_REASONS.find(r => r.value === reason)?.label}: ${additionalNotes}`
        : CANCELLATION_REASONS.find(r => r.value === reason)?.label || reason;

      await subscriptionService.cancelSubscription(subscriptionId, fullReason);
      
      toast({
        title: 'Subscription Cancelled',
        description: `The subscription for ${patientName} has been cancelled.`,
      });
      
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Cancellation Failed',
        description: error.message || 'Failed to cancel subscription',
        variant: 'destructive',
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            Cancel Subscription
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to cancel the subscription for{' '}
            <span className="font-medium text-gray-900">{patientName}</span>?
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Cancellation Reason *</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent className="z-[600]">
                {CANCELLATION_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Additional Notes (optional)</Label>
            <Textarea
              placeholder="Add any additional details about this cancellation..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelling}>Keep Subscription</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleCancel();
            }}
            disabled={cancelling || !reason}
            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
          >
            {cancelling ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Canceling...
              </>
            ) : (
              'Cancel Subscription'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelSubscriptionDialog;

